import React, { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, Typography } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SortableBannerItem from './SortableBannerItem';
import { MediaI } from '../../../models/home';

interface MultiBannerMediaDropzoneProps {
  items: MediaI[];
  setItems: (items: MediaI[]) => void;
  onRemove: (index: number) => void;
  title: string;
}

const MultiBannerMediaDropzone: React.FC<MultiBannerMediaDropzoneProps> = ({
  items,
  setItems,
  onRemove,
  title,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith('image/') || file.type.startsWith('video/')
      );

      const nuevos = files.map((file, i) => ({
        id: `new`,
        path_imagen: URL.createObjectURL(file),
        media: file,
        orden: (items.length + i + 1).toString(),
        seccion_id: 'Home',
      }));

      setItems([...items, ...nuevos] as MediaI[]);
    },
    [items, setItems]
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((file) =>
      file.type.startsWith('image/') || file.type.startsWith('video/')
    );

    const nuevos = files.map((file, i) => ({
      id: `new`,
      path_imagen: URL.createObjectURL(file),
      media: file,
      orden: (items.length + i + 1).toString(),
      seccion_id: 'Home',
    }));

    setItems([...items, ...nuevos] as MediaI[]);
    
    // Resetear el input para permitir seleccionar el mismo archivo nuevamente
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleClickDropzone = () => {
    fileInputRef.current?.click();
  };

  const handleUrlChange = (index: number, url: string) => {
    const updated = [...items];
    updated[index].path_imagen = url;
    setItems(updated);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordenados = arrayMove(items, oldIndex, newIndex);

        // Reasignar el campo 'orden' según la nueva posición
        const conOrden = reordenados.map((item, i) => ({ ...item, orden: (i + 1).toString() }));
        setItems(conOrden);
      }
    }
  };

  return (
    <Box sx={{ marginTop: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>

      <Box sx={{ marginBottom: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <Box
          onClick={handleClickDropzone}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            border: '2px dashed #ccc',
            padding: 3,
            textAlign: 'center',
            borderRadius: 2,
            backgroundColor: '#fafafa',
            flex: 1,
            minWidth: 250,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#f0f0f0',
              borderColor: '#999',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, marginBottom: 1 }}>
            <AddPhotoAlternateIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <VideoLibraryIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            Haz clic o arrastra y suelta aquí
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Imágenes y videos
          </Typography>
        </Box>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={rectSortingStrategy}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            flexWrap: 'wrap',
            gap: 2,
          }}>
            {items.map((item, index) => (
              <SortableBannerItem
                key={item.id}
                item={item}
                index={index}
                onRemove={onRemove}
                onUrlChange={handleUrlChange}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>

      {items.length === 0 && (
        <Box
          sx={{
            padding: 4,
            textAlign: 'center',
            color: 'text.secondary',
            backgroundColor: '#fafafa',
            borderRadius: 2,
            marginTop: 2,
          }}
        >
          <Typography variant="body1">
            No hay banners. Haz clic o arrastra imágenes/videos para comenzar.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MultiBannerMediaDropzone;

